import * as React from 'react';
import { useCallback, useRef } from 'react';

import { map } from 'lodash';

import { PostCard, Notice, Toast, IToastRefHandles } from '@components';
import { pluralize } from '@frontend/app/utils/strings';
import { backendServerApiEndpoint } from '@frontend/applications/Shared/serviceHosts';
import { useBackendServerFetch } from '@frontend/app/clients/backendServerClient';
import { logger } from '@common';

import { IPostCardSocialPost } from '@components';
import { ISocialPostArtifact } from '../useFetchSocialPostData';
import { saveSocialData } from '../saveSocialPostData';

import styles from './SocialPostsList.scss';

interface IProps {
  posts: ISocialPostArtifact[];
  onPostClick?(post: ISocialPostArtifact): void;
  postDisplayName: string;
  onRefresh?(): void;
}

export const SocialPostsList: React.FunctionComponent<IProps> = (props) => {
  const { posts, postDisplayName, onRefresh } = props;
  const toastRef = useRef<IToastRefHandles>(null);
  const { backendServerFetchResponse } = useBackendServerFetch();

  const onPostClick = useCallback((postData: ISocialPostArtifact, event: React.MouseEvent) => {
    if (props.onPostClick) {
      props.onPostClick(postData);
      event.preventDefault();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.onPostClick]);

  const onSaveSuccess = useCallback(() => {
    const toast = toastRef.current;
    if (toast) {
      toast.showMessage({
        content: 'Post was updated successfully',
        type: 'success',
      });
    }
    if (onRefresh) {
      onRefresh();
    }
  }, [onRefresh]);

  const handleUnassignPost = useCallback(async (post: IPostCardSocialPost) => {
    const toast = toastRef.current;
    if (!post?.mention_id) {
      if (toast) {
        toast.showMessage({
          content: 'Cannot unassign post: missing mention ID',
          type: 'error',
        });
      }
      return;
    }

    const params = {
      mention_ids: [post.mention_id.toString()],
      program_names: [],
      program_ids: [],
      manual_assignment: true,
    };

    try {
      const result = await saveSocialData(`${backendServerApiEndpoint()}/social_mention`, params, backendServerFetchResponse);
      if (result) {
        onSaveSuccess();
      }
    } catch (error) {
      logger.error('Failed to unassign post:', error);
      if (toast) {
        toast.showMessage({
          content: 'Failed to unassign post',
          type: 'error',
        });
      }
    }
  }, [backendServerFetchResponse, onSaveSuccess]);

  const handleUnassignGroup = useCallback(async (post: IPostCardSocialPost) => {
    const toast = toastRef.current;

    if (!post?.mention_id) {
      if (toast) {
        toast.showMessage({
          content: 'Cannot unassign group: missing mention ID',
          type: 'error',
        });
      }
      return;
    }

    const params = {
      mention_ids: [post.mention_id.toString()],
      community_ids: [],
      manual_assignment: true,
    };

    try {
      const result = await saveSocialData(`${backendServerApiEndpoint()}/social_mention`, params, backendServerFetchResponse);
      if (result) {
        onSaveSuccess();
      }
    } catch (error) {
      logger.error('Failed to unassign group:', error);
      if (toast) {
        toast.showMessage({
          content: 'Failed to unassign group',
          type: 'error',
        });
      }
    }
  }, [backendServerFetchResponse, onSaveSuccess]);

  return (
    <div className={styles.SocialPostsList}>
      <div>
        {posts && posts.length === 0 && (
          <Notice type="disabled">
            This member has no
            {' '}
            {postDisplayName}
            .
          </Notice>
        )}
        {posts && posts.length > 0 && (
          <span>
            Showing
            {' '}
            {posts.length}
            {' '}
            {pluralize(posts.length, postDisplayName)}
          </span>
        )}
        <div className={styles.postListContainer}>
          {posts && posts.length > 0 && map(posts, (postData, index) => (
            <div
              key={index}
              className={styles.postContainer}
              onClick={(event) => onPostClick(postData, event)}
            >
              <PostCard
                isQa={false}
                // eslint-disable-next-line  @typescript-eslint/no-explicit-any
                post={postData as any}
                hideValueSection
                key={index}
                handleUnassignPost={handleUnassignPost}
                handleUnassignGroup={handleUnassignGroup}
              />
            </div>
          ))}
        </div>
      </div>
      <Toast ref={toastRef} />
    </div>
  );
};
